import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, MapPin, ArrowUpRight, Video, Globe2 } from "lucide-react";
import { StarsDisplay } from "@/components/coachly/StarRating";
import { TrustRow } from "@/components/coachly/TrustBadges";
import CoachBadges from "@/components/coachly/CoachBadges";
import SaveCoachButton from "@/components/coachly/SaveCoachButton";
import { MARKETPLACE } from "@/constants/testIds";

export default function CoachCard({ coach, index = 0, onUnsaved }) {
  const price = coach.price_per_session ?? coach.hourly_rate;
  const duration = coach.session_duration_minutes || 60;
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      data-testid={MARKETPLACE.coachCard(coach.slug)}
      className="group relative bg-white border border-slate-200/80 rounded-2xl overflow-hidden hover:shadow-[0_20px_60px_-20px_rgba(37,99,235,0.25)] hover:border-slate-300 transition-all duration-300"
    >
      {/* Save button lives OUTSIDE the profile link so tapping it doesn't
          navigate to the coach page. */}
      <SaveCoachButton
        coachSlug={coach.slug}
        variant="icon"
        onChange={(saved) => { if (!saved && typeof onUnsaved === "function") onUnsaved(coach.slug); }}
      />

      <Link to={`/coach/${coach.slug}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden bg-slate-100">
          <img
            src={coach.avatar_url}
            alt={coach.name}
            className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0" />

          {/* Verified pill kept on the photo top-left. Other badges are moved
              below the headline where they read better. */}
          {coach.is_verified && (
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-[11px] font-medium text-slate-900">
              <BadgeCheck className="w-3.5 h-3.5 text-[#2563EB]" strokeWidth={2.4} />
              Verified
            </div>
          )}
          <div className="absolute bottom-3 right-3 inline-flex items-center bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-[11px] font-semibold text-slate-900">
            ${price} <span className="opacity-60 ml-1">/ {duration}m</span>
          </div>

          {/* Session mode chips overlayed bottom-left */}
          <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5 max-w-[70%]">
            {coach.online_available && (
              <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-white/90 backdrop-blur text-[10.5px] font-semibold text-[#2563EB]">
                <Video className="w-3 h-3" strokeWidth={2.4} /> Online
              </span>
            )}
            {coach.in_person_available && (
              <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-white/90 backdrop-blur text-[10.5px] font-semibold text-[#059669]">
                <MapPin className="w-3 h-3" strokeWidth={2.4} /> In person
              </span>
            )}
          </div>
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-display font-semibold text-lg leading-tight tracking-tight text-slate-900 truncate">
                {coach.name}
              </h3>
              <div className="mt-1 flex items-center gap-1.5 text-[12.5px] text-slate-500">
                <MapPin className="w-3.5 h-3.5" strokeWidth={2} />
                <span className="truncate">{coach.city}</span>
              </div>
            </div>
            <ArrowUpRight
              className="w-5 h-5 text-slate-400 group-hover:text-[#2563EB] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
              strokeWidth={2}
            />
          </div>

          {/* Trust badges — filter out verified since it's already on the photo. */}
          <CoachBadges
            badges={(coach.badges || []).filter((b) => b.key !== "verified")}
            variant="card"
            className="mt-2.5"
          />

          <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed line-clamp-2 min-h-[36px]">
            {coach.headline}
          </p>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {coach.specialties.slice(0, 3).map((s) => (
              <span
                key={s}
                className="text-[10.5px] tracking-wide uppercase font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-600"
              >
                {s}
              </span>
            ))}
          </div>

          {(coach.languages || []).length > 0 && (
            <div className="mt-2.5 inline-flex items-center gap-1.5 text-[11.5px] text-slate-500">
              <Globe2 className="w-3.5 h-3.5" strokeWidth={2} />
              <span className="truncate">{(coach.languages || []).join(" · ")}</span>
            </div>
          )}

          <div className="mt-3">
            <TrustRow coach={coach} dense testId={`coach-card-trust-${coach.slug}`} />
          </div>

          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StarsDisplay value={coach.avg_rating} size={13} />
              <span data-testid={MARKETPLACE.coachCardRating(coach.slug)} className="text-[13px] font-semibold text-slate-900">
                {coach.review_count > 0 ? coach.avg_rating.toFixed(1) : "—"}
              </span>
              <span data-testid={MARKETPLACE.coachCardCount(coach.slug)} className="text-[12.5px] text-slate-500">
                ({coach.review_count})
              </span>
            </div>
            <span
              data-testid={MARKETPLACE.coachCardCta(coach.slug)}
              className="text-[12.5px] font-semibold text-[#2563EB] group-hover:text-[#1d4ed8]"
            >
              View profile
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
